import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { fmtMoney, fmtSigned } from "./format";
import { DocUploader } from "./deskui";
import { FactSheet, usePageOpener } from "./FactSheet";

// One building's file (R1-OD-18/19), opened as the single side panel every desk selection uses
// (R1-OD-11). It reads the projection-only /api/owner/building/{ref}: the mark line by line, the
// mark-history EVENT list (never a smoothed line), and the documents. The undefendable state
// (R1-OD-67/68/69) renders NO number of any kind — it names the one missing fact and states plainly
// that the market context implies nothing about this building.

// F30: the cap-rate breakdown, rendered ONLY from values the Underwriter stored with the mark (rate_card
// is assembled in the projection from stored fields — the UI does no arithmetic). States the cap rate,
// then itemises each adjustment with where its fact came from, or one neutral line for an older mark.
// F117 (founder 30 Sep): the "p.7" inside a rationale line is a LINK — the same mint-on-click
// capability the fact sheet's citations use, opening the physical page the line cites (F119).
function AdjLine({ adj, fileId, open, testid }) {
  const cls = "rate-step" + (adj.muted ? " rate-step-muted" : "");
  const parts = adj.page != null && fileId ? /^(.*?)(p\.\d+)(.*)$/.exec(adj.line) : null;
  if (!parts) return <div className={cls} data-testid={testid}>{adj.line}</div>;
  return (
    <div className={cls} data-testid={testid}>
      {parts[1]}
      {/* A button, not an anchor: the capability is minted by the click (F110), so there is no
          honest href to right-click — a placeholder one would 404 on "open in new tab". */}
      <button type="button" className="rate-page-link" onClick={open(fileId, adj.page)}
              data-testid="rate-page-link">{parts[2]}</button>
      {parts[3]}
    </div>
  );
}

function RateCard({ card }) {
  const open = usePageOpener();
  if (!card) return null;
  const cap = `Cap rate ${card.cap_rate_pct}%`;
  const noi = card.noi != null
    ? ` · NOI $${Number(card.noi).toLocaleString("en-US")}${card.noi_basis_label ? ` (${card.noi_basis_label})` : ""}`
    : "";
  const asof = card.as_of ? ` · as of ${card.as_of}` : "";
  return (
    <div className="rate-card" data-testid="rate-card">
      <div className="rate-head" data-testid="rate-head">{cap}{noi}{asof}</div>
      {card.itemised ? (
        <div className="rate-steps">
          <div className="rate-step" data-testid="rate-base">
            Base rate {card.base_rate_pct}%{card.base_context ? ` — ${card.base_context}` : ""}
          </div>
          {(card.adjustments || []).map((a, i) => (
            <AdjLine key={i} adj={a} fileId={card.file_id} open={open}
                     testid={"rate-adj-" + i} />
          ))}
          {card.clamp_line && <div className="rate-step rate-step-muted" data-testid="rate-clamp">{card.clamp_line}</div>}
          <div className="rate-step rate-applied" data-testid="rate-applied">Applied {card.applied_pct}%</div>
        </div>
      ) : (
        <p className="rate-note" data-testid="rate-preitemised">{card.note}</p>
      )}
    </div>
  );
}

function MarkReader({ file, buildingRef, onCorrected }) {
  const m = file.mark;
  const { authedFetch } = useAuth();
  const cell = (file.row && file.row.rungs && file.row.rungs.mark) || {};
  if (file.in_conflict || m.conflict) {
    return (
      <div className="mr-conflict" data-testid="mark-conflict">
        <div className="mr-status-red">A claim conflict is open on this building.</div>
        <p>Neither party can view the mark until it is resolved. Nobody can load the other side's
          identity, evidence or portfolio.</p>
      </div>
    );
  }
  // Transient system states (founder ruling, Step B) — read the resolved book cell, which carries the
  // state + owner-facing copy. `needs a person` is OUR bug (never undefendable, never a number);
  // `working` is a recompute in flight; `waiting on you` links the open decision; `reading` is in flight.
  if (cell.state === "needs_a_person") {
    return <div className="mr-empty" data-testid="mark-needs-person">{cell.message}</div>;
  }
  if (cell.state === "working") {
    return <div className="mr-empty" data-testid="mark-working">{cell.message}</div>;
  }
  if (cell.state === "waiting_on_you") {
    return <div className="mr-empty" data-testid="mark-waiting">{cell.message}</div>;
  }
  if (m.undefendable) {
    return (
      <div className="mr-undef" data-testid="mark-undefendable">
        <div className="mr-undef-eyebrow">THE UNDERWRITER · NO MARK I CAN DEFEND</div>
        <p className="mr-undef-lead">
          I could write you a number. I could not defend it, so I am not going to. There is no value,
          band or range here on purpose.
        </p>
        <div className="mr-undef-block">
          <div className="mr-undef-label">What would resolve it</div>
          <p data-testid="undef-missing">{m.missing}</p>
          <p className="mr-undef-arrival" data-testid="undef-onarrival">{m.on_arrival}</p>
        </div>
        {m.unread_note && (
          <div className="mr-undef-block" data-testid="undef-unread-pages">
            <div className="mr-undef-label">Pages I couldn't read</div>
            <p>{m.unread_note}</p>
          </div>
        )}
        {m.not_needed && m.not_needed.length > 0 && (
          <div className="mr-undef-block" data-testid="undef-notneeded">
            <div className="mr-undef-label">Not needed — don't go hunting for</div>
            <ul>{m.not_needed.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
        )}
        <div className="mr-undef-market" data-testid="undef-market">
          <div className="mr-undef-label">The market around it</div>
          <p className="mr-undef-class">{m.market_context_ref}</p>
          <p className="mr-undef-disclaimer">
            These are other people's buildings. I will not infer your income from somebody else's —
            this implies nothing about what yours is worth.
          </p>
        </div>
      </div>
    );
  }
  if (m.value == null) {
    const intake = file.intake || {};
    const inFlight = cell.state === "reading" || (intake.state && intake.state !== "none");
    const msg = inFlight && intake.line ? intake.line
      : (cell.state === "reading" && cell.message) ? cell.message
      : "No mark. I need a rent roll or a T-12 so I can value it.";
    return <div className="mr-empty" data-testid="mark-upstream">{msg}</div>;
  }
  return (
    <>
      {/* F121 (founder 30 Sep): a re-read in flight speaks in the Underwriter's own voice, above the
          number it has not replaced yet. The reason arrives worked out; nothing here stores it. */}
      {cell.re_reading && (
        <div className="mr-working" data-testid="mark-re-reading">
          <span className="v4-work" data-testid="mark-re-reading-agent">
            <span className="v4-work-dot" />{cell.re_reading_agent}
          </span>
          <span className="v4-work-line">{cell.re_reading_line}</span>
        </div>
      )}
      <div className="mr-mark v4-markcard" data-juno-ink="dark" data-testid="mark-value">
        <span className="v4-markcard-label">THE MARK</span>
        <div className="mr-value-row">
          <span className="mr-value">{fmtMoney(m.value)}</span>
          {m.delta != null && m.delta !== 0 && (
            <span className={"mr-delta " + (m.delta < 0 ? "delta-down" : "delta-up")}
                  data-testid="mark-delta">
              {m.delta < 0 ? "▼" : "▲"} {fmtSigned(m.delta)}
            </span>
          )}
          <span className="mr-asof">as of {m.as_of}</span>
        </div>
        {m.basis && m.basis[0] && (
          <div className="mr-noi-basis" data-testid="mark-noi-basis">{m.basis[0]}</div>
        )}
      </div>
      {m.caveats && m.caveats.length > 0 && (
        <div className="mr-caveats" data-testid="mark-caveats">
          <div className="mr-caveats-label">What this mark rests on</div>
          {m.caveats.map((c, i) => (
            <div key={i} className={"mr-caveat" + (c.kind === "ask" ? " mr-caveat-ask" : "")}
                 data-testid={"mark-caveat-" + i}>
              {c.text}
              {/* ITEM 8 (founder ruling 11 Oct): the ONE TAP behind "Tell me if that's wrong". It
                  opens the submarket question on this building; the answer is human_stated, the
                  building re-marks, and the mark history says the submarket was corrected. */}
              {c.correct === "submarket" && (
                <button className="mr-caveat-fix" data-testid="mark-correct-submarket"
                        onClick={async () => {
                          try {
                            await authedFetch(
                              `/owner/commands/building/${buildingRef}/submarket-correction`,
                              { method: "POST" });
                            if (onCorrected) onCorrected();
                          } catch (e) { /* the desk re-reads; nothing is lost */ }
                        }}>That's wrong — let me set it</button>
              )}
            </div>
          ))}
        </div>
      )}
      {m.unread_note && (
        <div className="mr-unread" data-testid="mark-unread-pages">{m.unread_note}</div>
      )}
      {m.cap_rate_card ? <RateCard card={m.cap_rate_card} /> : (
        <div className="mr-ledger">
          {(m.basis || []).slice(1).map((line, i) => (
            <div key={i} className="mr-line" data-testid={"mark-basis-" + (i + 1)}>{line}</div>
          ))}
        </div>
      )}
    </>
  );
}

const CONTEST_FACTS = { noi: "Net operating income", gla_sf: "Building size (GLA)", year_built: "Year built" };
const CONTEST_CLASS = { submarket: "Submarket", subtype: "Retail type", building_class: "Building class",
                        asset_type: "Building type" };

function contestableLines(report) {
  // facts the file placed + the classification parts. Each is contestable with a document or a reason.
  const lines = [];
  const promoted = new Set((report?.facts_promoted || []).map((f) => f.field));
  Object.entries(CONTEST_FACTS).forEach(([field, label]) =>
    lines.push({ kind: "fact", field, label, canDoc: true }));
  Object.entries(CONTEST_CLASS).forEach(([field, label]) =>
    lines.push({ kind: "classification", field, label, canDoc: false }));
  return lines.map((l) => ({ ...l, placed: promoted.has(l.field) }));
}

function ContestPanel({ building, report, onContested }) {
  const { authedFetch } = useAuth();
  const lines = contestableLines(report);
  const [sel, setSel] = useState(`${lines[0].kind}:${lines[0].field}`);
  const [mode, setMode] = useState("reason");
  const [text, setText] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const line = lines.find((l) => `${l.kind}:${l.field}` === sel) || lines[0];

  const submit = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      let out;
      if (mode === "document") {
        const fd = new FormData();
        fd.append("target_kind", line.kind);
        fd.append("target_field", line.field);
        fd.append("file", fileObj);
        out = await authedFetch(`/owner/commands/building/${building.building_ref}/contest-document`,
          { method: "POST", body: fd });
      } else {
        out = await authedFetch(`/owner/commands/building/${building.building_ref}/contest`,
          { method: "POST", body: { target_kind: line.kind, target_field: line.field, reason_text: text } });
      }
      setResult(out); setText(""); setFileObj(null);
      onContested && onContested();
    } catch (e) {
      setErr(e.detail || "That contest could not be recorded.");
    } finally { setBusy(false); }
  };

  const canSend = mode === "document" ? !!fileObj : text.trim().length > 0;
  return (
    <section className="panel-section" data-testid="contest-panel">
      <h3 className="panel-h">Contest a line</h3>
      <p className="set-note">Every fact and classification I used is yours to contest — with a document
        or a reason. You contest the inputs; I re-mark and tell you what moved, or why nothing did.</p>
      <select className="set-input" data-testid="contest-line" value={sel}
              onChange={(e) => { setSel(e.target.value); setMode("reason"); setResult(null); }}>
        {lines.map((l) => (
          <option key={`${l.kind}:${l.field}`} value={`${l.kind}:${l.field}`}>
            {l.label}{l.kind === "fact" && !l.placed ? " (not on file yet)" : ""}
          </option>
        ))}
      </select>
      <div className="contest-modes" style={{ marginTop: 8 }}>
        <button className={`contest-mode ${mode === "reason" ? "on" : ""}`} data-testid="contest-mode-reason"
                onClick={() => setMode("reason")}>With a reason</button>
        {line.canDoc && (
          <button className={`contest-mode ${mode === "document" ? "on" : ""}`} data-testid="contest-mode-document"
                  onClick={() => setMode("document")}>With a document</button>
        )}
      </div>
      {mode === "reason" ? (
        <textarea className="set-input" data-testid="contest-reason" style={{ marginTop: 8, minHeight: 64 }}
                  placeholder="Tell me what it should be, and why." value={text}
                  onChange={(e) => setText(e.target.value)} />
      ) : (
        <input type="file" className="set-input" data-testid="contest-file" style={{ marginTop: 8 }}
               accept=".pdf,.xlsx" onChange={(e) => setFileObj(e.target.files[0] || null)} />
      )}
      <button className="mlog-primary" data-testid="contest-send" disabled={busy || !canSend}
              style={{ marginTop: 8 }} onClick={submit}>{busy ? "Working…" : "Contest this line"}</button>
      {err && <div className="panel-err" data-testid="contest-err">{err}</div>}
      {result && (
        <div className="contest-result" data-testid="contest-result" style={{ marginTop: 10 }}>
          <div className={result.moved ? "contest-moved" : "contest-nomove"}>
            {result.moved ? "The mark moved" : "Nothing changed"}
          </div>
          <p className="set-note" data-testid="contest-explanation">{result.explanation}</p>
        </div>
      )}
    </section>
  );
}

// F21/F34/F35/F38: "Add a document" at the top of the building file — the confirm-before-send uploader
// (shared with step 4 of Add a building), on the same scan → Librarian path, with the F27-hardened
// surfacing (a non-2xx is a visible failure, never a silent "done").
function AddDocument({ buildingRef, onFiled }) {
  return (
    <section className="panel-section" data-testid="add-document">
      <h3 className="panel-h">Add a document</h3>
      <p className="set-note">An offering memo, a rent roll — a single PDF or .xlsx (.xls isn't readable,
        save it as .xlsx). Every line of the mark traces back to a page you give me.</p>
      <DocUploader buildingRef={buildingRef} onFiled={onFiled} testidPrefix="add-document" />
    </section>
  );
}

export function BuildingPanel({ buildingRef, seed, onClose, onRemoved }) {
  const { authedFetch } = useAuth();
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  // Bumped on every (re-)read of the file so the fact sheet re-reads with it — its page links carry a
  // short-lived capability, so a stale sheet is also a sheet whose links are about to expire.
  const [reads, setReads] = useState(0);

  const loadFile = useCallback(() => {
    setReads((r) => r + 1);
    authedFetch(`/owner/building/${buildingRef}`).then(setFile).catch(
      (e) => setErr(e.detail || "Could not load this building."));
    authedFetch(`/owner/building/${buildingRef}/report`).then(setReport).catch(() => { /* secondary */ });
  }, [buildingRef, authedFetch]);

  useEffect(() => {
    setFile(null); setReport(null); setErr("");
    loadFile();
    // F38: opening the file clears the desk's neutral new-marker on this building. Fire-and-forget.
    authedFetch(`/owner/commands/building/${buildingRef}/seen`, { method: "POST" }).catch(() => {});
  }, [loadFile, buildingRef, authedFetch]);

  // F42: live updates, no F5. While this building's document is still being read (mark cell reading /
  // working, or a document in received/extracting), re-fetch in place every ~5s — no reload, no scroll
  // jump (setFile/setReport just re-render). Stop when nothing is in flight; pause while the tab hidden.
  useEffect(() => {
    if (!file) return undefined;
    if (typeof document !== "undefined" && document.hidden) return undefined;
    const cell = (file.row && file.row.rungs && file.row.rungs.mark) || {};
    const cellPending = cell.working === true || cell.empty === "reading" || cell.empty === "upstream_incomplete";
    const docPending = (file.documents || []).some(
      (d) => d.state === "received" || d.state === "extracting" || d.state === "reading");
    if (!cellPending && !docPending) return undefined;
    const t = setTimeout(loadFile, 5000);
    return () => clearTimeout(t);
  }, [file, loadFile]);

  // F116 (founder 30 Sep): the file closes on the × or Esc, and on NOTHING else. A click outside it
  // used to close it, so a half-typed contest reason was lost to a stray click; the scrim is now
  // inert (pointer-events: none in deskv4.css) so a click on another row just switches buildings.
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);

  const saveName = async () => {
    try {
      await authedFetch(`/owner/commands/building/${buildingRef}/name`,
        { method: "POST", body: { name: nameDraft.trim() } });
      setEditingName(false); loadFile();
    } catch (e) { setErr(e.detail || "Could not rename this building."); }
  };

  const REASONS = [
    { value: "sold", label: "Sold" },
    { value: "transferred", label: "Transferred out" },
    { value: "never_mine", label: "Never mine" },
    { value: "none_given", label: "Prefer not to say" },
  ];

  const confirmRemove = async () => {
    setBusy(true);
    try {
      const body = { reason: reason || "none_given" };
      if (reason === "sold" && price.trim()) body.sale_price = parseInt(price.replace(/[^\d]/g, ""), 10);
      await authedFetch(`/owner/commands/building/${buildingRef}/remove`, { method: "POST", body });
      if (onRemoved) onRemoved();
      onClose();
    } catch (e) { setErr(e.detail || "Could not remove this building."); setBusy(false); }
  };

  // F115 (founder 30 Sep): the file opens on what the clicked ROW already knows — the name, the
  // address and the disposition — so it never flashes an internal `bld-…` id or asserts a
  // disposition nobody was asked for. Everything else stays empty until the read lands.
  const head = file || seed || {};
  const headline = head.name || head.address || null;
  const dispLabel = head.disposition === "selling" ? "ACTIVELY SELLING"
    : head.disposition === "open" ? "OPEN TO PRICE" : null;

  return (
    <div className="v4-over" data-testid="building-panel">
      <div className="v4-over-scrim" />
      <aside className="v4-over-panel">
        <div className="v4-over-top">
          <div className="v4-over-title">
            {dispLabel && (
              <span className="v4-over-eyebrow" data-testid="panel-disposition">{dispLabel}</span>
            )}
            {editingName ? (
              <div className="name-edit" data-testid="name-edit">
                <input className="mlog-input" data-testid="name-input" autoFocus value={nameDraft}
                       placeholder="Name this building (optional)"
                       onChange={(e) => setNameDraft(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") saveName(); }} />
                <button className="chrome-btn chrome-btn-primary" data-testid="name-save" onClick={saveName}>Save</button>
                <button className="chrome-btn" data-testid="name-cancel" onClick={() => setEditingName(false)}>Cancel</button>
              </div>
            ) : (
              <button className="v4-over-h v4-over-h-edit" data-testid="panel-ref"
                      title="Click to name this building"
                      onClick={() => { setNameDraft((file && file.name) || ""); setEditingName(true); }}>
                {headline || (file ? "Name this building" : "\u00a0")}
              </button>
            )}
            {head.name && head.address && (
              <div className="v4-over-sub" data-testid="panel-address">{head.address}</div>
            )}
          </div>
          <button className="v4-over-close" data-testid="panel-close" onClick={onClose}>×</button>
        </div>
        <div className="v4-over-body">
        {err && <div className="panel-err" data-testid="panel-err">{err}</div>}
        {!file && !err && <div className="panel-loading">Loading…</div>}
        {file && (
          <>
            <section className={"panel-section mark-section"
              + (((file.row && file.row.rungs && file.row.rungs.mark) || {}).working
                 || (((file.row && file.row.rungs && file.row.rungs.mark) || {}).empty === "reading")
                 ? " is-reading" : "")}
              data-testid="panel-mark-section">
              <h3 className="panel-h">The mark</h3>
              <MarkReader file={file} buildingRef={buildingRef} onCorrected={loadFile} />
            </section>

            <section className="panel-section" data-testid="panel-fact-sheet">
              <h3 className="panel-h">Everything I read from your file</h3>
              <FactSheet buildingRef={buildingRef} refreshKey={reads} />
            </section>

            {file.owner_stated && file.owner_stated.length > 0 && (
              <section className="panel-section" data-testid="owner-stated">
                <h3 className="panel-h">You told me</h3>
                <ul className="owner-stated-list">
                  {file.owner_stated.map((s, i) => (
                    <li key={i} className="owner-stated-item"
                        data-testid={"owner-stated-" + s.field_path}>{s.line}</li>
                  ))}
                </ul>
                <p className="set-note">These are your words — kept as stated, never turned into a
                  sourced fact, and shown apart from what I read from your documents.</p>
              </section>
            )}

            {/* F21c: contest a line whenever the building HAS a mark; hidden only when there is no mark. */}
            {file.mark && file.mark.value != null && !file.in_conflict
              && <ContestPanel building={file} report={report} onContested={loadFile} />}

            {file.contests && file.contests.length > 0 && (
              <section className="panel-section" data-testid="contest-history">
                <h3 className="panel-h">Recent contests</h3>
                <ul className="owner-stated-list">
                  {file.contests.map((c, i) => (
                    <li key={i} className="owner-stated-item" data-testid={"contest-item-" + i}>
                      <span className={c.moved ? "contest-moved" : "contest-nomove"}>
                        {c.moved ? "moved" : "no change"}</span>{" · "}{c.explanation}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="panel-section">
              <h3 className="panel-h">Mark history</h3>
              {file.mark_history.events.length === 0 ? (
                <p className="panel-muted" data-testid="history-empty">
                  {file.mark_history.empty_reason || "Nothing to plot yet."}
                </p>
              ) : (
                <ul className="mh-list" data-testid="history-list">
                  {file.mark_history.events.map((e, i) => (
                    <li key={i} className="mh-item">
                      <div className="mh-top">
                        <span className="mh-value">{fmtMoney(e.value)}</span>
                        <span className="mh-asof">{e.as_of}</span>
                      </div>
                      <div className="mh-cause">{e.cause}</div>
                      <div className="mh-source">source · {e.source}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="panel-section">
              <h3 className="panel-h">Documents behind it</h3>
              {file.documents.length === 0 ? (
                <p className="panel-muted" data-testid="docs-empty">No documents filed to this building yet.</p>
              ) : (
                <ul className="doc-list" data-testid="docs-list">
                  {file.documents.map((d, i) => (
                    <li key={i}>{d.name} <span className={"doc-state" + (d.state_label === "read" ? " doc-state-done" : "")}
                        data-testid={"doc-state-" + i}>· {d.state_label}</span></li>
                  ))}
                </ul>
              )}
              <AddDocument buildingRef={buildingRef} onFiled={loadFile} />
            </section>

            <section className="panel-section panel-remove">
              {!removing ? (
                <button className="remove-open" data-testid="remove-open" onClick={() => setRemoving(true)}>
                  Remove this building
                </button>
              ) : (
                <div className="remove-box" data-testid="remove-box">
                  <div className="remove-lead">Take this out of your book. Your file and mark history stay
                    on the record — it simply leaves the book and every total, and the agents stop.</div>
                  <div className="remove-reasons">
                    {REASONS.map((r) => (
                      <button key={r.value}
                              className={"remove-reason" + (reason === r.value ? " remove-reason-on" : "")}
                              data-testid={"remove-reason-" + r.value}
                              onClick={() => setReason(r.value)}>{r.label}</button>
                    ))}
                  </div>
                  {reason === "sold" && (
                    <div className="remove-price">
                      <label className="set-label" htmlFor="remove-price-in">Sale price <span className="set-opt">optional</span></label>
                      <input id="remove-price-in" className="set-input" data-testid="remove-price"
                             value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$ —" />
                      <p className="set-note">Texas doesn't record sale prices, so this is one of the few
                        things I can never look up. It stays your word, never attributed to you, never shown
                        to a buyer — it only sharpens marks in your corridor.</p>
                    </div>
                  )}
                  {reason === "never_mine" && (
                    <p className="set-note" data-testid="remove-nevermine-note">This reverses the claim and
                      flags it for review — a building that should not have reached your desk.</p>
                  )}
                  <div className="remove-actions">
                    <button className="chrome-btn" data-testid="remove-cancel"
                            onClick={() => { setRemoving(false); setReason(null); setPrice(""); }}>Keep it</button>
                    <button className="remove-confirm" data-testid="remove-confirm" disabled={busy}
                            onClick={confirmRemove}>{busy ? "Removing…" : "Remove"}</button>
                  </div>
                </div>
              )}
            </section>
          </>
        )}
        </div>
      </aside>
    </div>
  );
}
